/**
 * PROTOTYPE — mobile-width stand-in for sheet + long-press.
 * Not a native spike. Question: does this analog feel native?
 */
import { useRef, useState } from 'react'
import { BLOCKS, EVENT, PEOPLE, type ProtoRef } from './seed'
import type { PrototypeState } from './usePrototypeState'

export function ContextualTasksMobile({ state }: { state: PrototypeState }) {
	const items = state.room === 'attendance' ? PEOPLE : BLOCKS
	const [picking, setPicking] = useState(false)
	const sheetOpen = state.open && !picking
	return (
		<div className="flex min-h-screen items-start justify-center bg-black py-6">
			<div className="relative h-[720px] w-[390px] overflow-hidden rounded-[2rem] border-4 border-zinc-700 bg-[#303535] text-white shadow-2xl">
				<header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
					<p className="text-xs font-bold uppercase tracking-wider text-[#88d8de]">
						{EVENT.name}
					</p>
					<div className="flex gap-1">
						{(
							[
								['attendance', 'Attend'],
								['plan', 'Plan'],
							] as const
						).map(([key, label]) => (
							<button
								key={key}
								type="button"
								onClick={() => state.setRoom(key)}
								className={`rounded px-2 py-1 text-[11px] font-semibold ${
									state.room === key
										? 'bg-[#88d8de] text-[#303535]'
										: 'text-gray-400'
								}`}
							>
								{label}
							</button>
						))}
					</div>
				</header>
				<div className="h-[520px] space-y-2 overflow-y-auto p-3 pb-28">
					<p className="text-[11px] text-gray-500">
						Long-press a row (T analog). + opens the sheet.
					</p>
					{items.map((item) => (
						<LongPressRow
							key={item.id}
							item={item}
							state={state}
							picking={picking}
							onPicked={() => setPicking(false)}
						/>
					))}
				</div>
				<button
					type="button"
					onClick={() =>
						state.open ? state.newTask() : state.openComposer()
					}
					className="absolute bottom-28 right-4 z-20 flex size-12 items-center justify-center rounded-full bg-[#ff585f] text-xl font-bold shadow-lg"
				>
					+
				</button>
				{picking && state.open ? (
					<button
						type="button"
						onClick={() => setPicking(false)}
						className="absolute inset-x-0 bottom-0 z-30 border-t border-[#88d8de]/40 bg-[#1b1f1f] px-4 py-3 text-left text-xs text-[#88d8de]"
					>
						Tap a highlighted row to add a Reference · Done
					</button>
				) : null}
				{sheetOpen ? (
					<div className="absolute inset-x-0 bottom-0 z-30 rounded-t-2xl border-t border-white/10 bg-[#1b1f1f] p-4 shadow-[0_-8px_40px_rgba(0,0,0,0.5)]">
						<div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
						<input
							autoFocus
							value={state.draft.action}
							onChange={(e) =>
								state.setDraft((d) => ({
									...d,
									action: e.target.value,
								}))
							}
							placeholder="What needs to happen?"
							className="w-full bg-transparent text-base text-white outline-none placeholder:text-gray-500"
						/>
						<div className="mt-2 flex flex-wrap gap-1">
							{state.draft.references.map((ref) => (
								<button
									key={ref.id}
									type="button"
									onClick={() => state.removeRef(ref.id)}
									className="rounded-full bg-[#88d8de]/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-[#88d8de]"
								>
									{ref.name} ×
								</button>
							))}
						</div>
						<div className="mt-3 flex gap-2">
							<button
								type="button"
								onClick={state.save}
								className="rounded-md bg-[#ff585f] px-3 py-1.5 text-xs font-bold"
							>
								Save
							</button>
							<button
								type="button"
								onClick={() => setPicking(true)}
								className="rounded-md bg-white/10 px-3 py-1.5 text-xs font-semibold"
							>
								Add reference
							</button>
							<button
								type="button"
								onClick={state.close}
								className="text-xs text-gray-400"
							>
								Close
							</button>
						</div>
						<p className="mt-2 text-[10px] text-gray-500">
							Long-press another row, or Add reference to get the
							sheet out of the way
						</p>
					</div>
				) : null}
			</div>
		</div>
	)
}

function LongPressRow({
	item,
	state,
	picking,
	onPicked,
}: {
	item: ProtoRef
	state: PrototypeState
	picking: boolean
	onPicked: () => void
}) {
	const timer = useRef<number | null>(null)
	const fired = useRef(false)
	const attached = state.draft.references.some((r) => r.id === item.id)

	const start = () => {
		fired.current = false
		timer.current = window.setTimeout(() => {
			fired.current = true
			if (state.open) state.addRef(item)
			else state.openComposer(item)
		}, 420)
	}
	const cancel = () => {
		if (timer.current) window.clearTimeout(timer.current)
		timer.current = null
	}

	return (
		<button
			type="button"
			onPointerDown={start}
			onPointerUp={cancel}
			onPointerLeave={cancel}
			onClick={(e) => {
				if (picking) {
					state.addRef(item)
					onPicked()
					return
				}
				if (fired.current) {
					e.preventDefault()
					fired.current = false
				}
			}}
			className={`flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left ${
				attached
					? 'border-[#88d8de] bg-[#88d8de]/15'
					: 'border-white/10 bg-white/5'
			}`}
		>
			<span className="flex size-9 items-center justify-center rounded-full bg-[#88d8de] text-xs font-bold text-[#303535]">
				{item.name
					.split(' ')
					.map((w) => w[0])
					.join('')}
			</span>
			<div>
				<p className="text-sm font-semibold">{item.name}</p>
				<p className="text-[10px] text-gray-500">
					{item.kind} · long-press
				</p>
			</div>
		</button>
	)
}
